import * as React from 'react';
import { tabbable } from 'tabbable';

import { Binding } from 'core/infrastructure';
import { Environment } from 'core/utils';

type FormatFunction = (option: any) => string;

export type Format = FormatFunction | string | undefined;

export function formatValue(value: any, formatFunctionOrPropertyName: Format): string {

	if (value === null)
		return '';

	// Uso el displayProperty
	if (typeof formatFunctionOrPropertyName === 'string') {
		return Binding.getValue(value, formatFunctionOrPropertyName).toString();
	}

	if (typeof formatFunctionOrPropertyName === 'function') {
		return formatFunctionOrPropertyName(value);
	}

	// Si no tengo un displayProperty, intento usar alguna propiedad
	// que parezca razonable
	const defaultProperties = ['name', 'Name', 'description', 'Description', 'fiscalName', 'FiscalName'];
	for (const p of defaultProperties) {
		if (typeof value[p] !== 'undefined') {
			return value[p].toString();
		}
	}

	return value.toString();
}

export function formatAsElementId(linkedProperty: string, formId: string) {
	const id = linkedProperty.replace(/\./g, '-').replace(/\[|\]/g, '_').replace(/\s/g, '');
	return formId ? formId + '-' + id : id;
}

export function whenEnterKey(doProcess: () => void) {

	return function (e: React.KeyboardEvent<any>) {

		// En el caso de android, cuando el type no es text, en lugar de un botón de Enter (Ir)
		// muestra un botón de Siguiente (Sig) equivalente al tab, lo que imposibilita al usuario
		// pulsar Enter. Para ñapearlo, si es android (y sólo si es android) equiparo el tab al
		// Enter

		// Controlo keyCode para browser antiguos (como si fuese a funcionar ahí Ágora) y key para
		// browsers nuevos y eventos lanzados desde nuestro Keypad

		if (e.keyCode === 13 || e.key === 'Enter' || (Environment.isAndroid() && e.keyCode === 9)) {
			e.preventDefault();
			e.stopPropagation();
			doProcess();
		}
	};
}

export function whenEscKey(doProcess: () => void) {
	return function (e: React.KeyboardEvent<any>) {
		if (e.keyCode === 27 || e.key === 'Esc' || e.key === 'Escape') {
			e.preventDefault();
			e.stopPropagation();
			doProcess();
		}
	};
}

export function whenEspaceKey(doProcess: () => void) {

	return function (e: React.KeyboardEvent<any>) {
		if (e.keyCode === 32 || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			doProcess();
		}
	};
}

export function debounce(action: (...args: any[]) => void, delay?: number) {
	let timer: NodeJS.Timeout;
	return function (...rest: any[]) {
		clearTimeout(timer);
		timer = setTimeout(() => action(...rest), delay || 300);
	};
}

export function addLineBreaks(message: string) {
	if (!message || message === '')
		return '';

	return message
		.split('\n')
		.reduce((acc: React.ReactNode[], line: string, i: number) =>
			acc.concat([<span key={2 * i}>{line.trim()}</span>, <br key={2 * i + 1} />]), [])
		.slice(0, -1);
}

// Soporta shortcuts de la forma:
// "F1", "F2", "F3", "ctrl+a", "alt+a", "ctrl+shift+f", ...
export function addShortcuts(element: HTMLElement, shortcuts: { [shortcut: string]: () => void }) {


	if (!element)
		throw Error('addShortcuts debe ser llamado cuando el componente ya está montado');

	Object.entries(shortcuts).forEach(([shortcut, handler]) => {
		const parts = shortcut.split('+');
		const ctrlKey = parts.some(x => x.toLowerCase() === 'ctrl');
		const altKey = parts.some(x => x.toLowerCase() === 'alt');
		const shiftKey = parts.some(x => x.toLowerCase() === 'shift');
		const key = parts[parts.length - 1];

		// Necesito que pueda recibir el foco (cosa que no ocurre por defecto en los divs que engloban nuestras pantallas)
		// pero no quiero que saque el recuadro de foco horrible que pone el browser
		element.tabIndex = element.tabIndex || -1;
		element.style.outline = 'none';

		element.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.ctrlKey === ctrlKey && e.altKey === altKey && e.shiftKey === shiftKey && e.key === key) {
				handler();
				e.preventDefault();
			}
		});
	});
}

export function supportSelection(input: HTMLInputElement): boolean {
	// La lista de inputs que soportan selección sale del siguiente enlace
	// https://html.spec.whatwg.org/multipage/input.html#do-not-apply
	const selectionSupportedTypes = ['text', 'search', 'url', 'tel', 'password'];
	return selectionSupportedTypes.includes(input.type);
}

export function sendFocusToInitialElement(element: HTMLElement): void {
	// Intento activar el hijo inicial del elemento indicado.
	// De entre los elementos tabbables (aquellos a los que se puede llegar con el tabulador)
	// elijo primero el que tenga la clase auto-focus, y si no el primer tabbable
	const tabbables = tabbable(element);

	const target = tabbables.find(x => x.classList.contains('auto-focus')) || tabbables[0];

	if (target) {
		target.focus();
		// Si es un input seleccionamos el texto
		if (target.tagName === 'INPUT' && supportSelection(target as HTMLInputElement))
			(target as HTMLInputElement).select();
	}
}
